import { useState, useRef, useCallback } from 'react';

interface DragNumberInputProps {
  value: number;
  onChange: (v: number) => void;
  onDragStart?: () => void;
  step?: number;
  decimals?: number;
  /** Pixels of mouse travel per unit change. Lower = more sensitive. */
  sensitivity?: number;
  label: string;
  labelColorClass?: string;
  title?: string;
  className?: string;
}

/**
 * Numeric input with a draggable label — click the label and drag left/right to scrub the value.
 * Click without dragging (< 4px travel) focuses the text input instead.
 * Standard in Blender, After Effects, Unity Inspector.
 */
export default function DragNumberInput({
  value,
  onChange,
  onDragStart,
  step = 1,
  decimals = 1,
  sensitivity = 0.5,
  label,
  labelColorClass = 'text-text-tertiary',
  title,
  className = '',
}: DragNumberInputProps) {
  const safe = Number.isFinite(value) ? value : 0;
  const [draft, setDraft] = useState<string | null>(null);
  const committedRef = useRef(safe);
  const inputRef = useRef<HTMLInputElement>(null);

  // Drag state
  const dragRef = useRef<{ startX: number; startValue: number; moved: boolean } | null>(null);

  const commit = (raw: string) => {
    const normalized = raw.replace(',', '.').trim();
    let v: number;
    // Allow basic math expressions: digits, operators, parens, decimal point
    if (/^[0-9+\-*/.()\s]+$/.test(normalized)) {
      try {
        // eslint-disable-next-line no-new-func
        const result = new Function(`return (${normalized})`)() as unknown;
        const n = Number(result);
        v = Number.isFinite(n) ? n : committedRef.current;
      } catch {
        v = committedRef.current;
      }
    } else {
      const parsed = parseFloat(normalized);
      v = Number.isFinite(parsed) ? parsed : committedRef.current;
    }
    committedRef.current = v;
    onChange(v);
    setDraft(null);
  };

  const onLabelPointerDown = useCallback((e: React.PointerEvent<HTMLSpanElement>) => {
    // Only primary button
    if (e.button !== 0) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = { startX: e.clientX, startValue: safe, moved: false };
    onDragStart?.();
  }, [safe, onDragStart]);

  const onLabelPointerMove = useCallback((e: React.PointerEvent<HTMLSpanElement>) => {
    const drag = dragRef.current;
    if (!drag || !(e.buttons & 1)) return;
    const dx = e.clientX - drag.startX;
    if (!drag.moved && Math.abs(dx) < 4) return;
    drag.moved = true;
    const newVal = drag.startValue + dx * sensitivity;
    committedRef.current = newVal;
    onChange(newVal);
    setDraft(null);
  }, [onChange, sensitivity]);

  const onLabelPointerUp = useCallback((_e: React.PointerEvent<HTMLSpanElement>) => {
    const drag = dragRef.current;
    dragRef.current = null;
    if (!drag) return;
    // If we barely moved, treat as click → focus the input
    if (!drag.moved) {
      setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 0);
    }
  }, []);

  return (
    <div className={`flex items-center gap-1 flex-1 min-w-0 ${className}`}>
      <span
        onPointerDown={onLabelPointerDown}
        onPointerMove={onLabelPointerMove}
        onPointerUp={onLabelPointerUp}
        className={`font-mono text-[10px] font-semibold w-3 shrink-0 select-none cursor-ew-resize ${labelColorClass}`}
        title={title ?? label}
      >
        {label}
      </span>
      <input
        ref={inputRef}
        type="text"
        inputMode="decimal"
        title={title}
        value={draft ?? safe.toFixed(decimals)}
        onChange={(e) => setDraft(e.target.value)}
        onFocus={(e) => { setDraft(safe.toFixed(decimals)); e.target.select(); }}
        onBlur={(e) => commit(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { (e.target as HTMLInputElement).blur(); }
          if (e.key === 'ArrowUp') { e.preventDefault(); const v = safe + step; onChange(v); setDraft(null); }
          if (e.key === 'ArrowDown') { e.preventDefault(); const v = safe - step; onChange(v); setDraft(null); }
          if (e.key === 'Escape') { setDraft(null); (e.target as HTMLInputElement).blur(); }
          e.stopPropagation();
        }}
        className="flex-1 min-w-0 bg-bg-input border border-border text-text-primary rounded-md
                   px-2 py-[5px] font-mono text-xs text-right
                   hover:bg-bg-hover focus:outline-none focus:border-accent focus:shadow-focus
                   transition-all duration-normal"
      />
    </div>
  );
}
